import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { Alert, Button } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import type { SessionAccount } from '@super-mega/server/contracts/auth'
import { type ServiceOrder } from '@super-mega/server/contracts/order'
import {
  getAuditFormValues,
  getAuditSearch,
  useMemberCreatorOptions,
  type AuditFormValues
} from '@/features/access/index.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { OrderBillsDrawer } from '@/features/bill/index.ts'
import {
  OrderContractsDrawer,
  OrderContractStatusModal,
  type OrderContractStatusEditing
} from '@/features/contract/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import type { PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getBatchJobAlertType, getBatchJobStatusLabel } from '@/utils/batch-job.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { getPositiveIntegerIdSearchValue } from '@/utils/list-query.ts'
import { orderBatchActionItems } from './const/batch-actions.ts'
import { useOrderListFilterOptions } from './hooks/use-order-list-filter-options.ts'
import { useServiceListActions } from './hooks/use-service-list-actions.tsx'
import { OrderBatchUpdateModal, type OrderBatchEdit } from './order-batch-update-modal.tsx'
import { OrderListContainer } from './order-list-container.tsx'
import { DEFAULT_ORDER_LIST_SEARCH, type OrderListSearch } from './order-list-search.ts'
import { RentalForceDeleteModal } from './rental-force-delete-modal.tsx'
import { createServiceColumns } from './service-list-columns.tsx'

type ServiceOrderRecord = ServiceOrder

type OrderSearch = OrderListSearch

type OrderSearchFormValues = AuditFormValues &
  Pick<OrderSearch, 'keyword' | 'note' | 'serviceMode' | 'archiveStatus'> & {
    idSearch?: string
    businessId?: number[]
    customerId?: number[]
    salespersonId?: number[]
    subjectId?: number[]
    statusFilter?: OrderSearch['status']
  }

export function ServiceOrderList({
  search,
  navigateSearch,
  currentSession,
  compact = false,
  customerScoped = false,
  hideCreatedTimeFilter = false,
  defaultSearch = DEFAULT_ORDER_LIST_SEARCH
}: {
  search: OrderListSearch
  navigateSearch: PageTableNavigate<OrderListSearch>
  currentSession: SessionAccount
  compact?: boolean
  hideCreatedTimeFilter?: boolean
  customerScoped?: boolean
  defaultSearch?: OrderListSearch
}) {
  const [contractsOrder, setContractsOrder] = useState<Pick<ServiceOrderRecord, 'id' | 'orderNo'>>()
  const [contractStatusEditing, setContractStatusEditing] = useState<OrderContractStatusEditing>()
  const [billsOrder, setBillsOrder] = useState<Pick<ServiceOrderRecord, 'id' | 'orderNo'>>()
  const [batchEditing, setBatchEditing] = useState<OrderBatchEdit>()
  const [forceRemoving, setForceRemoving] = useState<{ order: ServiceOrderRecord } | { orders: ServiceOrderRecord[] }>()
  const router = useRouter()
  const trpc = useTRPC()
  const memberCreator = useMemberCreatorOptions('order.service.read', search.creatorMemberId)
  const navigate = useNavigate({ from: '/orders/service/' })
  const canSubmit = currentSession.isSuperAdmin || currentSession.permissions.includes('order.service.create')
  const canManageContracts =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('order.service.') && !permission.endsWith('.read')
    ) ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('contract.document.') && !permission.endsWith('.read')
    )
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('order.service.') && !permission.endsWith('.read')
    )
  const orders = useQuery({
    ...trpc.order.service.list.queryOptions(search),
    placeholderData: keepPreviousData
  })
  const filterOptions = useOrderListFilterOptions({ businessType: 'valueAdded', rows: orders.data?.data })
  const {
    selectedIds,
    setSelection,
    batchRemove,
    batchBySearch,
    busy,
    searchBatchWarnings,
    searchBatchJobId,
    setSearchBatchJobId,
    searchBatchStatus,
    refreshBatchUpdate,
    confirmBatchRemove,
    confirmSearchBatch,
    confirmRemove,
    refreshOrders,
    openContractsBySearch
  } = useServiceListActions({
    search,
    navigateSearch,
    currentSession,
    rows: orders.data?.data,
    total: orders.data?.total ?? 0
  })
  const table = usePageTableNavigation<OrderSearch, OrderSearchFormValues>({
    defaultSearch,
    getFormInitialValues: (currentSearch) => ({
      ...getAuditFormValues(currentSearch),
      businessId: currentSearch.businessId,
      customerId: currentSearch.customerId,
      salespersonId: currentSearch.salespersonId,
      subjectId: currentSearch.subjectId,
      idSearch: currentSearch.id === undefined ? undefined : String(currentSearch.id),
      keyword: currentSearch.keyword,
      serviceMode: currentSearch.serviceMode,
      note: currentSearch.note,
      archiveStatus: currentSearch.archiveStatus,
      statusFilter: currentSearch.status
    }),
    getSearchFromForm: (values) => ({
      ...getAuditSearch(values),
      businessId: values.businessId,
      customerId: values.customerId,
      salespersonId: values.salespersonId,
      subjectId: values.subjectId,
      id: getPositiveIntegerIdSearchValue(values.idSearch),
      keyword: values.keyword?.trim() || undefined,
      serviceMode: values.serviceMode,
      note: values.note?.trim() || undefined,
      archiveStatus: values.archiveStatus,
      status: values.statusFilter
    }),
    navigate: navigateSearch,
    search,
    showTotal: (total) => `共 ${total} 个服务订单`
  })

  const openCreate = () => {
    void navigate({ to: '/orders/service/new', search: {}, state: { serviceOrderListReturnSearch: search } })
  }
  const openOrder = (orderId: number) => {
    void navigate({
      params: { orderId: String(orderId) },
      search: {},
      state: { serviceOrderListReturnSearch: search },
      to: '/orders/service/$orderId/edit'
    })
  }

  return (
    <OrderListContainer
      compact={compact}
      title="服务订单"
      content="管理一次性及期限服务，跟进办理、服务期限与周期费用。"
    >
      {contractsOrder && (
        <OrderContractsDrawer
          key={contractsOrder.id}
          order={{ type: 'service', ...contractsOrder }}
          onClose={() => setContractsOrder(undefined)}
        />
      )}
      {contractStatusEditing && (
        <OrderContractStatusModal editing={contractStatusEditing} onClose={() => setContractStatusEditing(undefined)} />
      )}
      {billsOrder && (
        <OrderBillsDrawer order={{ type: 'service', ...billsOrder }} onClose={() => setBillsOrder(undefined)} />
      )}
      {orders.isError ? (
        <Alert
          action={
            <Button onClick={() => void orders.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(orders.error, '服务订单列表加载失败')}
          type="error"
        />
      ) : null}
      {searchBatchWarnings.length > 0 && (
        <Alert
          className="mb-4"
          showIcon
          type="warning"
          title="部分文件需要人工清理"
          description={
            <ul>
              {[...new Set(searchBatchWarnings)].map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}
      {searchBatchJobId && searchBatchStatus.data && (
        <Alert
          className="mb-4"
          showIcon
          type={getBatchJobAlertType({
            state: searchBatchStatus.data.state,
            failedCount: searchBatchStatus.data.failedCount,
            warningCount: searchBatchStatus.data.warnings.length
          })}
          title={`按搜索批量任务：${getBatchJobStatusLabel(searchBatchStatus.data.state)}（${searchBatchStatus.data.processed}/${searchBatchStatus.data.total}）`}
          description={
            <>
              <div>{`成功 ${searchBatchStatus.data.successCount} 个，失败 ${searchBatchStatus.data.failedCount} 个。任务 ID：${searchBatchJobId}`}</div>
              <ul>
                {[...new Set(searchBatchStatus.data.warnings)].map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </>
          }
          action={
            <Button size="small" onClick={() => void refreshBatchUpdate()}>
              刷新列表
            </Button>
          }
        />
      )}
      {forceRemoving && (
        <RentalForceDeleteModal
          {...forceRemoving}
          kind="service"
          onClose={() => setForceRemoving(undefined)}
          onDeleted={refreshOrders}
        />
      )}
      <ProTable<ServiceOrderRecord, OrderSearchFormValues>
        columns={createServiceColumns({
          isSuperAdmin: currentSession.isSuperAdmin,
          search,
          memberCreator,
          filterOptions,
          canManage,
          canSubmit,
          onDelete: confirmRemove,
          onForceDelete: (order) => setForceRemoving({ order }),
          canReadBills: currentSession.isSuperAdmin || currentSession.permissions.includes('bill.read'),
          onView: (orderId) =>
            void navigate({
              to: '/orders/service/$orderId',
              params: { orderId: String(orderId) },
              state: { serviceOrderListReturnSearch: search }
            }),
          busy,
          onOpen: openOrder,
          onContracts: setContractsOrder,
          onBills: setBillsOrder
        }).filter(
          (column) =>
            (!customerScoped || column.dataIndex !== 'customerId') &&
            (!hideCreatedTimeFilter || column.dataIndex !== 'createdAtRange')
        )}
        columnsState={{
          persistenceKey: customerScoped
            ? 'customer-documents-service:table'
            : compact
              ? 'order-group-service:table'
              : 'orders-service:table',
          persistenceType: 'localStorage',
          defaultValue: compact
            ? {
                customerName: { show: false },
                occupancyMode: { show: false },
                leaseAction: { show: false },
                updatedAt: { show: false }
              }
            : undefined
        }}
        dataSource={orders.data?.data ?? []}
        headerTitle={`服务订单（${orders.data?.total ?? 0}）`}
        loading={{ delay: 150, spinning: orders.isFetching }}
        onReset={table.onReset}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            const sort = Array.isArray(sorter) ? sorter[0] : sorter
            void navigateSearch({
              replace: true,
              search: (previous) => ({
                ...previous,
                current: 1,
                sortField: 'createdAt',
                sortOrder: sort.order === 'ascend' ? 'asc' : 'desc'
              })
            })
          }
          /* eslint-enable max-params */
        }
        onSubmit={table.onSubmit}
        options={{ reload: () => void orders.refetch() }}
        pagination={{ ...table.pagination, total: orders.data?.total ?? 0 }}
        formRef={table.formRef}
        rowSelection={
          canManage || canSubmit || canManageContracts
            ? {
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelection({ search, ids: keys.map(Number) }),
                getCheckboxProps: () => ({ disabled: busy || orders.isFetching })
              }
            : false
        }
        rowKey="id"
        scroll={{ x: compact ? '100rem' : '140rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: compact, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() => [
          <AuthorizedDropdown
            resource="order.service"
            key="batch-actions"
            disabled={busy || orders.isFetching || selectedIds.length === 0}
            menu={{
              items: orderBatchActionItems({
                contractLabel: '批量打印合同',
                contractDisabled: selectedIds.length > 20,
                canContract: true,
                canManageContracts,
                canManage,
                isOwner: currentSession.isSuperAdmin
              }),
              onClick: ({ key }) => {
                if (key === 'contract') {
                  window.open(
                    router.buildLocation({ to: '/orders/service/contracts/batch', search: { ids: selectedIds } }).href,
                    '_blank',
                    'noopener,noreferrer'
                  )
                  return
                }

                if (key === 'contractStatus') {
                  setContractStatusEditing({
                    orderType: 'service',
                    target: { mode: 'ids', ids: [...selectedIds] }
                  })
                  return
                }
                if (key === 'salesperson' || key === 'customer' || key === 'group') {
                  setBatchEditing({
                    kind: key,
                    orders: (orders.data?.data ?? []).filter((order) => selectedIds.includes(order.id))
                  })
                  return
                }
                if (key === 'forceDelete') {
                  const selected = new Set(selectedIds)
                  setForceRemoving({
                    orders: (orders.data?.data ?? []).filter((order) => selected.has(order.id))
                  })
                } else {
                  confirmBatchRemove()
                }
              }
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              disabled={busy || orders.isFetching || selectedIds.length === 0}
              loading={batchRemove.isPending}
              icon={<ListChecks aria-hidden size={16} />}
            >
              批量操作 <ChevronDown aria-hidden size={14} />
            </Button>
          </AuthorizedDropdown>,
          <AuthorizedDropdown
            resource="order.service"
            key="search-batch-actions"
            disabled={busy || orders.isFetching || !orders.data?.total}
            menu={{
              items: orderBatchActionItems({
                contractLabel: '批量打印合同',
                contractDisabled: !orders.data?.total || orders.data.total > 20,
                canContract: true,
                canManageContracts,
                canManage,
                isOwner: currentSession.isSuperAdmin
              }),
              onClick: ({ key }) => {
                if (key === 'contractStatus') {
                  setContractStatusEditing({
                    orderType: 'service',
                    target: { mode: 'search', search, expectedCount: orders.data?.total ?? 0 }
                  })
                } else if (key === 'salesperson' || key === 'customer' || key === 'group') {
                  setBatchEditing({ kind: key, search, expectedCount: orders.data?.total ?? 0 })
                } else if (key === 'contract') {
                  void openContractsBySearch()
                } else if (key === 'delete' || key === 'forceDelete') {
                  confirmSearchBatch(key)
                }
              }
            }}
          >
            <Button disabled={busy || orders.isFetching || !orders.data?.total} loading={batchBySearch.isPending}>
              按搜索批量操作 <ChevronDown aria-hidden size={14} />
            </Button>
          </AuthorizedDropdown>,
          ...(canSubmit
            ? [
                <Authorized key="new" permissions={['order.service.create']}>
                  <Button icon={<Plus aria-hidden size={16} />} key="new" onClick={openCreate} type="primary">
                    新建服务订单
                  </Button>
                </Authorized>
              ]
            : [])
        ]}
      />
      {batchEditing ? (
        <OrderBatchUpdateModal
          onQueued={setSearchBatchJobId}
          editing={batchEditing}
          orderKind="service"
          onClose={() => setBatchEditing(undefined)}
          onSaved={refreshOrders}
        />
      ) : null}
    </OrderListContainer>
  )
}
